import { contractBook } from "@eulerxyz/core-sdk"
import type { AbiParametersToPrimitiveTypes } from "abitype"
import {
  Address,
  type ContractFunctionName,
  Hex,
  decodeAbiParameters,
  encodeAbiParameters,
  getAbiItem,
} from "viem"
import type { ContractFunctionResult } from "../evc"
import { type OracleType, type OracleTypeAbi } from "./types"

function checkIsType(
  oracleInfo: ContractFunctionResult<OracleTypeAbi>,
  name: ContractFunctionName<OracleTypeAbi>,
): boolean {
  try {
    const abiItem = getAbiItem({
      abi: contractBook.oracleTypes.abi,
      name,
    })
    if (abiItem.outputs[0].components.length !== Object.keys(oracleInfo).length)
      return false

    return Boolean(
      encodeAbiParameters(abiItem.outputs, [
        oracleInfo,
      ] as unknown as AbiParametersToPrimitiveTypes<typeof abiItem.outputs>),
    )
  } catch {
    return false
  }
}
export function isEulerRouter(
  oracleInfo: ContractFunctionResult<OracleTypeAbi>,
): oracleInfo is OracleType<"getEulerRouterInfo"> {
  return checkIsType(oracleInfo, "getEulerRouterInfo")
}
export function isEulerRouterLazyCheck(
  oracleInfo: ContractFunctionResult<OracleTypeAbi>,
): oracleInfo is OracleType<"getEulerRouterInfo"> {
  return "fallbackOracle" in oracleInfo && "governor" in oracleInfo
}
export function isChainlinkOracle(
  oracleInfo: ContractFunctionResult<OracleTypeAbi>,
): oracleInfo is OracleType<"getChainlinkOracleInfo"> {
  return checkIsType(oracleInfo, "getChainlinkOracleInfo")
}

export function isChronicleOracle(
  oracleInfo: ContractFunctionResult<OracleTypeAbi>,
): oracleInfo is OracleType<"getChronicleOracleInfo"> {
  return checkIsType(oracleInfo, "getChronicleOracleInfo")
}

export function isLidoOracle(
  oracleInfo: ContractFunctionResult<OracleTypeAbi>,
): oracleInfo is OracleType<"getLidoOracleInfo"> {
  return checkIsType(oracleInfo, "getLidoOracleInfo")
}

export function isPythOracle(
  oracleInfo: ContractFunctionResult<OracleTypeAbi>,
): oracleInfo is OracleType<"getPythOracleInfo"> {
  return checkIsType(oracleInfo, "getPythOracleInfo")
}

export function isRedstoneCoreOracle(
  oracleInfo: ContractFunctionResult<OracleTypeAbi>,
): oracleInfo is OracleType<"getRedstoneCoreOracleInfo"> {
  return checkIsType(oracleInfo, "getRedstoneCoreOracleInfo")
}

export function isUniswapV3Oracle(
  oracleInfo: ContractFunctionResult<OracleTypeAbi>,
): oracleInfo is OracleType<"getUniswapV3OracleInfo"> {
  return checkIsType(oracleInfo, "getUniswapV3OracleInfo")
}

export function isCrossAdapter(
  oracleInfo: ContractFunctionResult<OracleTypeAbi>,
): oracleInfo is OracleType<"getCrossAdapterInfo"> {
  return checkIsType(oracleInfo, "getCrossAdapterInfo")
}

export function isOndoOracle(
  oracleInfo: ContractFunctionResult<OracleTypeAbi>,
): oracleInfo is OracleType<"getOndoOracleInfo"> {
  return checkIsType(oracleInfo, "getOndoOracleInfo")
}

export function isPendleOracleLazyCheck(
  oracleInfo: ContractFunctionResult<OracleTypeAbi>,
): oracleInfo is OracleType<"getPendleOracleInfo"> {
  if ("pendleMarket" in oracleInfo && "twapWindow" in oracleInfo) {
    return true
  }
  return false
}

export function isCrossAdapterLazyCheck(
  oracleInfo: ContractFunctionResult<OracleTypeAbi>,
): oracleInfo is OracleType<"getCrossAdapterInfo"> {
  if (
    "base" in oracleInfo &&
    "oracleBaseCrossInfo" in oracleInfo &&
    "oracleCrossQuoteInfo" in oracleInfo
  )
    return true
  return false
}
export function isUniswapAdapter(
  oracleInfo: ContractFunctionResult<OracleTypeAbi>,
): oracleInfo is OracleType<"getUniswapV3OracleInfo"> {
  return checkIsType(oracleInfo, "getUniswapV3OracleInfo")
}

export function isUniswapAdapterLazyCheck(
  oracleInfo: ContractFunctionResult<OracleTypeAbi>,
): oracleInfo is OracleType<"getUniswapV3OracleInfo"> {
  return "tokenA" in oracleInfo && "tokenB" in oracleInfo
}

export function isERC4626Oracle(
  resolvedAssets: readonly Address[] | undefined,

  position: number,
): boolean {
  if (
    Array.isArray(resolvedAssets) &&
    position === 0 &&
    resolvedAssets.length === 1
  ) {
    return true
  } else if (
    Array.isArray(resolvedAssets) &&
    position > 0 &&
    resolvedAssets.length === 2
  ) {
    return true
  }

  return false
}

export function deepCheckIfIsRedstoneClasicOracle(
  internalOracles: ContractFunctionResult<OracleTypeAbi>,
  name: string,
  oracleInfoInternal?: Hex,
): string {
  if (
    "feedDescription" in internalOracles &&
    internalOracles?.feedDescription &&
    //  It should return "Redstone Price Feed for X token "
    internalOracles?.feedDescription
      ?.toLowerCase()
      .includes("redstone")
  ) {
    // @info RedstoneClassicOracles arrives with the name of Chainlink,
    // we need to check the descrition to update the logo and the type
    return "RedstoneClassicOracle"
  }
  if (name === "ChainlinkOracle" && oracleInfoInternal) {
    const abiItem = getAbiItem({
      abi: contractBook.oracleTypes.abi,
      name: "getChainlinkOracleInfo",
    })

    const _baseOracles = decodeAbiParameters(
      abiItem.outputs,
      oracleInfoInternal,
    )
    const base = _baseOracles[0] as OracleType<"getChainlinkOracleInfo">

    return deepCheckIfIsRedstoneClasicOracle(base, name, undefined)
  }

  return name
}
